<template>
  <div>
    <b-dropdown class="dropdown-notification" menu-class="dropdown-menu-media" right variant="link" no-caret
      toggle-class="p-0">
      <template #button-content>
        <div class="notification-trigger" aria-label="Notificações">
          <feather-icon icon="BellIcon" size="16" class="icon" />
          <span v-if="unreadCount > 0" class="badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
        </div>
      </template>

      <!-- Custom Content matching plan.md visual -->
      <div class="notification-menu-content">
        <div class="header">
          <span class="title">Notificações</span>
          <div class="actions">
            <label class="filter" @click.stop>
              <input type="checkbox" v-model="onlyUnreadFlag" />
              <span class="filter-text">Apenas não lidas</span>
            </label>
            <button class="action-btn" type="button" @click.stop="markAllAsRead" title="Marcar todas como lidas">
              Marcar todas
            </button>
            <button class="action-btn" type="button" @click.stop="openAllNotifications"
              title="Ver todas as notificações">
              Ver todas
            </button>
          </div>
        </div>

        <vue-perfect-scrollbar :settings="perfectScrollbarSettings" class="list scroll-area">
          <div v-if="isLoadingInitial" class="empty">Carregando...</div>
          <div v-else-if="!visibleNotifications.length" class="empty">Nenhuma notificação</div>

          <button v-for="n in visibleNotifications" :key="n.id" class="item" :class="{ 'item--unread': !n.isRead }"
            type="button" @click="handleNotificationClick(n)">
            <div class="meta">
              <span class="item-title">{{ n.title }}</span>
              <span v-if="n.module" class="item-module">{{ n.module }}</span>
            </div>
            <span class="item-message">{{ n.message }}</span>
            <span class="item-time">{{ formatDate(n.createdAt) }}</span>
          </button>
        </vue-perfect-scrollbar>
      </div>
    </b-dropdown>

    <!-- Modal for All Notifications -->
    <b-modal id="all-notifications-modal" v-model="isModalVisible" hide-header hide-footer size="lg" body-class="p-0"
      centered>
      <all-notifications @close="isModalVisible = false" />
    </b-modal>
  </div>
</template>

<script>
import { BDropdown, BModal } from 'bootstrap-vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { useSignalR } from '@/plugins/signalr'
import { computed, ref } from '@vue/composition-api'
import AllNotifications from './AllNotifications.vue'

export default {
  components: {
    BDropdown,
    BModal,
    VuePerfectScrollbar,
    AllNotifications,
  },
  setup(props, { root }) {
    // Note: In Vue 2 + Composition API plugin, we use root for $bvModal access if needed, 
    // but in template we can use $bvModal directly.

    const api = useSignalR()
    const {
      notifications,
      unreadCount,
      markAsRead,
      markAllAsRead,
      clearAll,
      isLoadingInitial,
      visibleNotifications,
      onlyUnread,
      setOnlyUnread
    } = api

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const onlyUnreadFlag = computed({
      get: () => onlyUnread.value,
      set: (val) => setOnlyUnread(!!val)
    })

    const formatDate = (dateStr) => {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
      })
    }

    const handleNotificationClick = (notification) => {
      if (!notification.isRead) {
        markAsRead(notification.id)
      }
    }

    const isModalVisible = ref(false)

    const openAllNotifications = () => {
      isModalVisible.value = true
    }

    return {
      notifications, // keep for compatibility if needed, but we use visibleNotifications
      visibleNotifications,
      unreadCount,
      markAllAsRead,
      clearAll,
      isLoadingInitial,
      perfectScrollbarSettings,
      formatDate,
      handleNotificationClick,
      onlyUnreadFlag,
      openAllNotifications,
      isModalVisible
    }
  },
}
</script>

<style lang="scss" scoped>
:root {
  --notif-bg: #ffffff;
  --notif-text: #111827;
  --notif-muted: #6b7280;
  --notif-border: rgba(0, 0, 0, 0.08);
  --notif-border-strong: rgba(0, 0, 0, 0.12);
  --notif-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
  --notif-accent: #ef4444;
  --notif-surface: #f9fafb;
}

.notification-trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  cursor: pointer;
  color: var(--notif-muted, #6b7280);

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
}

.icon {
  width: 16px;
  height: 16px;
  color: #998f8a;
}

.badge {
  position: absolute;
  right: 1px;
  background: var(--notif-accent, #ef4444);
  color: #fff;
  font-size: 10px;
  line-height: 1;
  padding: 3px 5px;
  border-radius: 999px;
  min-width: 18px;
  text-align: center;
  border: 2px solid #fff;
}

.notification-menu-content {
  width: 380px;
  background: var(--notif-bg, #fff);
  overflow: hidden;
  border-radius: 12px;
  /* Box shadow is handled by b-dropdown usually, but we ensure content style */
}

.header {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid var(--notif-border, rgba(0, 0, 0, 0.06));
}

.title {
  font-weight: 600;
  color: var(--notif-text, #111827);
  font-size: 1rem;
}

.actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 0.8rem;
}

.filter {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  margin: 0;
  color: var(--notif-muted, #6b7280);
  font-size: 0.85rem;
  user-select: none;

  input {
    cursor: pointer;
  }
}

.filter-text {
  user-select: none;
}

.action-btn {
  background: transparent;
  border: 1px solid var(--notif-border, rgba(0, 0, 0, 0.1));
  border-radius: 6px;
  padding: 4px 10px;
  color: var(--notif-text, #374151);
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;

  &:hover {
    background: var(--notif-surface, #f3f4f6);
    border-color: var(--notif-border-strong, rgba(0, 0, 0, 0.2));
  }
}

.list {
  max-height: 420px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: #f9fafb;
  /* Light bg for list area */
}

.empty {
  padding: 24px;
  color: var(--notif-muted, #6b7280);
  text-align: center;
  font-size: 0.9rem;
}

.item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  text-align: left;
  border-radius: 8px;
  border: 1px solid var(--notif-border, rgba(0, 0, 0, 0.06));
  background: var(--notif-bg, #fff);
  width: 100%;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;

  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.item--unread {
  background: #fff;
  border-left: 3px solid var(--notif-accent, #ef4444);
  /* Optional: slightly different bg to indicate unread? Plan says var(--notif-surface) */
  /* But border-left is a strong indicator */
}

.meta {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
  width: 100%;
}

.item-title {
  font-weight: 600;
  color: var(--notif-text, #111827);
  font-size: 0.9rem;
}

.item-module {
  font-size: 10px;
  color: var(--notif-muted, #6b7280);
  background: var(--notif-surface, #f3f4f6);
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.item-message {
  color: #4b5563;
  font-size: 0.85rem;
  line-height: 1.4;
}

.item-time {
  font-size: 11px;
  color: var(--notif-muted, #9ca3af);
  align-self: flex-end;
  margin-top: 4px;
}
</style>
